import React from 'react'

/** Generic pulsing placeholder block */
function Pulse({ className }: { className: string }) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
}

/** Skeleton for a single subject card - mirrors the real card layout */
function SubjectCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Coloured card header */}
      <div className="bg-gray-50 px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-2">
          {/* Emoji placeholder */}
          <div className="w-9 h-9 bg-gray-200 rounded-xl animate-pulse" />
          {/* Badge placeholder */}
          <Pulse className="h-5 w-20 rounded-full" />
        </div>
        {/* Subject name */}
        <Pulse className="h-5 w-3/4 rounded-md mt-3" />
        {/* Description */}
        <Pulse className="h-3 w-1/2 rounded-md mt-2" />
      </div>

      {/* Mastery bar */}
      <div className="px-5 py-2 border-b border-gray-50 space-y-1.5">
        <div className="flex items-center justify-between">
          <Pulse className="h-3 w-12 rounded" />
          <Pulse className="h-3 w-20 rounded" />
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full w-2/5 bg-gray-200 rounded-full animate-pulse" />
        </div>
      </div>

      {/* Topic chips */}
      <div className="px-5 py-3 border-b border-gray-50">
        <div className="flex flex-wrap gap-1.5">
          <Pulse className="h-5 w-16 rounded-full" />
          <Pulse className="h-5 w-20 rounded-full" />
          <Pulse className="h-5 w-14 rounded-full" />
        </div>
      </div>

      {/* CTA button */}
      <div className="px-5 py-4">
        <Pulse className="h-10 w-full rounded-xl" />
      </div>
    </div>
  )
}

/**
 * Full-page skeleton for SubjectSelectPage.
 * Matches the header → greeting → mode-toggle → subject-grid layout so
 * there is no jarring layout shift when the real content loads.
 */
export function SubjectPageSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-100 rounded-lg animate-pulse" />
            <Pulse className="h-4 w-24 rounded-md" />
          </div>
          <div className="flex items-center gap-3">
            <Pulse className="h-4 w-16 rounded-md" />
            <Pulse className="h-4 w-10 rounded-md" />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Greeting */}
        <div className="mb-6 space-y-2">
          <Pulse className="h-7 w-52 rounded-lg" />
          <Pulse className="h-4 w-64 rounded-md" />
          <Pulse className="h-3 w-48 rounded-md" />
        </div>

        {/* Study mode toggle */}
        <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl">
          {/* Active tab */}
          <div className="flex-1 bg-white rounded-lg shadow-sm px-2 py-2.5 flex items-center justify-center">
            <Pulse className="h-4 w-14 rounded-md" />
          </div>
          {/* Inactive tabs */}
          {[0, 1].map(i => (
            <div key={i} className="flex-1 px-2 py-2.5 flex items-center justify-center">
              <Pulse className="h-4 w-16 rounded-md" />
            </div>
          ))}
        </div>

        {/* Subject cards - 4 cards matches the typical 4–6 subjects a student sees */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map(i => (
            <SubjectCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  )
}
